import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getConsent, setConsent, revokeConsent, isDNT, CONSENT_STATES } from '../utils/consent';

const CookieConsent = ({ onConsentChange }) => {
  const [visible, setVisible] = useState(false);
  const [showManage, setShowManage] = useState(false);

  useEffect(() => {
    const consent = getConsent();
    if (consent === CONSENT_STATES.PENDING && !isDNT()) {
      const timer = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(timer);
    }
    // If DNT is on and no prior choice, treat as rejected silently
    if (isDNT() && consent === CONSENT_STATES.PENDING) {
      setConsent(CONSENT_STATES.REJECTED);
    }
  }, []);

  const handleAccept = () => {
    setConsent(CONSENT_STATES.ACCEPTED);
    setVisible(false);
    setShowManage(false);
    if (onConsentChange) onConsentChange(true);
  };

  const handleReject = () => {
    revokeConsent();
    setVisible(false);
    setShowManage(false);
    if (onConsentChange) onConsentChange(false);
  };

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-[60] px-4 pb-4"
      role="dialog"
      aria-label="Cookie consent"
      data-testid="cookie-consent-banner"
    >
      <div className="max-w-3xl mx-auto bg-carbon shadow-xl border border-white/10">
        {!showManage ? (
          /* Main banner */
          <div className="p-5 md:p-6">
            <p className="text-sm text-white/80 leading-relaxed mb-4">
              We use cookies to understand how visitors use this website and to improve the experience.
              Analytics data is collected anonymously using Google Analytics and PostHog.
              No personal data is shared with third parties for advertising.{' '}
              <Link to="/privacy" className="text-golden underline hover:text-golden/80">Privacy Policy</Link>
              {' '}&middot;{' '}
              <Link to="/cookie-policy" className="text-golden underline hover:text-golden/80">Cookie Policy</Link>
            </p>
            <div className="flex flex-wrap items-center gap-3">
              <button
                onClick={handleAccept}
                className="bg-golden text-carbon px-5 py-2 text-sm font-semibold hover:bg-golden/90 transition-colors"
                data-testid="cookie-accept-btn"
              >
                Accept cookies
              </button>
              <button
                onClick={handleReject}
                className="border border-white/30 text-white px-5 py-2 text-sm font-semibold hover:bg-white/10 transition-colors"
                data-testid="cookie-reject-btn"
              >
                Reject all
              </button>
              <button
                onClick={() => setShowManage(true)}
                className="text-white/50 text-sm hover:text-white/80 transition-colors underline"
                data-testid="cookie-manage-btn"
              >
                Manage preferences
              </button>
            </div>
          </div>
        ) : (
          /* Manage preferences */
          <div className="p-5 md:p-6">
            <h3 className="text-base font-heading font-semibold text-white mb-4">Cookie preferences</h3>
            <div className="space-y-4 mb-6">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-sm font-semibold text-white">Essential cookies</p>
                  <p className="text-xs text-white/50">Required for the website to function. Cannot be disabled.</p>
                </div>
                <span className="text-xs text-golden font-semibold mt-1">Always on</span>
              </div>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-sm font-semibold text-white">Analytics cookies</p>
                  <p className="text-xs text-white/50">Google Analytics and PostHog. Help us understand how visitors use the site. Data is anonymous and aggregated.</p>
                </div>
                <span className="text-xs text-white/40 mt-1">Optional</span>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <button
                onClick={handleAccept}
                className="bg-golden text-carbon px-5 py-2 text-sm font-semibold hover:bg-golden/90 transition-colors"
              >
                Accept all
              </button>
              <button
                onClick={handleReject}
                className="border border-white/30 text-white px-5 py-2 text-sm font-semibold hover:bg-white/10 transition-colors"
              >
                Essential only
              </button>
              <button
                onClick={() => setShowManage(false)}
                className="text-white/50 text-sm hover:text-white/80 transition-colors underline"
              >
                Back
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CookieConsent;
